import { useCallback, useEffect, useState } from 'react';
import { FileText, Loader2, Upload, ScrollText, ShieldCheck, Clock3 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { apiGet, authedFetch, API_BASE } from '@/lib/api';

/**
 * Question Bank — the fourth destination in My Gurukul, alongside Graphics,
 * Flashcards and Quiz.
 *
 * The bank is built BY the students who sat the papers: after an exam you upload
 * your paper, tagged by year, board, school, grade, subject, chapter and exam
 * name. A human reviews it before anyone else sees it, and only evidence a human
 * has recorded is ever shown as "frequently asked" — nothing is predicted.
 */
const FIELDS = [
  { key: 'year', label: 'Year', placeholder: '2026', required: true },
  { key: 'exam_name', label: 'Exam name', placeholder: 'Half Yearly', required: true },
  { key: 'subject', label: 'Subject', placeholder: 'Mathematics', required: true },
  { key: 'chapter', label: 'Chapter (optional)', placeholder: 'Chapter 3: Coordinate Geometry' },
  { key: 'school', label: 'School (optional)', placeholder: 'Leave blank to use yours' },
];

export default function QuestionBankPage() {
  const [data, setData] = useState(null);
  const [evidence, setEvidence] = useState(null);
  const [form, setForm] = useState({ year: '', exam_name: '', subject: '', chapter: '', school: '' });
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const [papers, ev] = await Promise.all([
        apiGet('/premium/v1/question-bank/papers'),
        apiGet('/premium/v1/question-bank/evidence').catch(() => null),
      ]);
      setData(papers); setEvidence(ev);
    } catch (e) { toast.error(e?.message || 'Could not open the Question Bank'); setData({ papers: [], my_uploads: [] }); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const upload = async () => {
    if (!file) return toast.error('Pick the paper first — a PDF or a clear photo');
    for (const f of FIELDS) if (f.required && !form[f.key].trim()) return toast.error(`${f.label} is needed`);
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      Object.entries(form).forEach(([k, v]) => fd.append(k, v));
      const res = await authedFetch(`${API_BASE}/premium/v1/question-bank/papers`, { method: 'POST', body: fd });
      if (!res.ok) throw new Error((await res.text()) || 'Upload failed');
      const out = await res.json();
      toast.success(out.message || 'Paper uploaded — thank you');
      setFile(null); setForm({ year: '', exam_name: '', subject: '', chapter: '', school: '' });
      load();
    } catch (e) { toast.error(e?.message || 'Could not upload that paper'); }
    setBusy(false);
  };

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 py-6 space-y-4" data-testid="question-bank-page">
      <div>
        <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight text-slate-900">Question Bank</h1>
        <p className="font-body text-sm text-slate-500 mt-1">
          Real papers from real exams. Upload the one you just sat, earn <strong>25 Veda Coins</strong> once it
          clears review, and the next student in your class walks in prepared.
        </p>
      </div>

      <div className="rounded-3xl border border-indigo-100 bg-white p-4 sm:p-5" data-testid="qb-upload-card">
        <p className="font-heading text-[13px] font-semibold text-slate-800 flex items-center gap-1.5">
          <Upload className="w-4 h-4 text-indigo-500" /> Add a paper you sat
        </p>
        <div className="grid sm:grid-cols-2 gap-2.5 mt-3">
          {FIELDS.map(f => (
            <label key={f.key} className="block">
              <span className="font-body text-[11px] text-slate-500">{f.label}</span>
              <Input value={form[f.key]} onChange={e => setForm(s => ({ ...s, [f.key]: e.target.value }))}
                placeholder={f.placeholder} className="rounded-xl text-sm mt-0.5" data-testid={`qb-field-${f.key}`} />
            </label>
          ))}
          <label className="block">
            <span className="font-body text-[11px] text-slate-500">Paper (PDF or photo, max 15 MB)</span>
            <input type="file" accept=".pdf,.jpg,.jpeg,.png,.webp"
              onChange={e => setFile(e.target.files?.[0] || null)} data-testid="qb-file-input"
              className="mt-0.5 block w-full text-[12px] font-body text-slate-600 file:mr-2 file:py-1.5 file:px-3 file:rounded-xl file:border-0 file:bg-indigo-50 file:text-indigo-700" />
          </label>
        </div>
        <Button onClick={upload} disabled={busy} className="rounded-xl mt-3" data-testid="qb-upload-btn">
          {busy ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null} Add to the Question Bank
        </Button>
        <p className="font-body text-[11px] text-slate-400 mt-2">
          Your board and class are filled in automatically. Every upload is reviewed by a human before it appears for anyone else.
        </p>
      </div>

      {!data && <p className="font-body text-sm text-slate-400 flex items-center gap-2" data-testid="qb-loading"><Loader2 className="w-4 h-4 animate-spin" /> Loading the bank…</p>}

      {data && (
        <div className="grid sm:grid-cols-2 gap-3">
          <div className="rounded-3xl border border-slate-200 bg-white p-4" data-testid="qb-approved">
            <p className="font-heading text-[13px] font-semibold text-slate-800 flex items-center gap-1.5">
              <FileText className="w-4 h-4 text-emerald-500" /> Papers for your class
            </p>
            {data.papers?.length ? (
              <ul className="mt-2 space-y-1.5">
                {data.papers.map(p => (
                  <li key={p.paper_id} data-testid={`qb-paper-${p.paper_id}`}>
                    <a href={p.file_url} target="_blank" rel="noreferrer"
                      className="font-body text-[13px] text-indigo-700 hover:underline">
                      {p.year} · {p.exam_name} · {p.subject}{p.chapter ? ` · ${p.chapter}` : ''}
                    </a>
                    {p.school && <span className="font-body text-[11px] text-slate-400"> — {p.school}</span>}
                  </li>
                ))}
              </ul>
            ) : <p className="font-body text-[13px] text-slate-500 mt-2">{data.empty_message}</p>}
          </div>

          <div className="rounded-3xl border border-slate-200 bg-white p-4" data-testid="qb-mine">
            <p className="font-heading text-[13px] font-semibold text-slate-800 flex items-center gap-1.5">
              <Clock3 className="w-4 h-4 text-amber-500" /> My uploads
            </p>
            {data.my_uploads?.length ? (
              <ul className="mt-2 space-y-1.5">
                {data.my_uploads.map(p => (
                  <li key={p.paper_id} className="font-body text-[13px] text-slate-700" data-testid={`qb-mine-${p.paper_id}`}>
                    {p.year} · {p.exam_name} · {p.subject}
                    <span className={`ml-1.5 text-[10px] font-heading px-2 py-0.5 rounded-full ${p.status === 'approved' ? 'bg-emerald-50 text-emerald-700' : p.status === 'rejected' ? 'bg-rose-50 text-rose-700' : 'bg-amber-50 text-amber-700'}`}>
                      {p.status.replace('_', ' ')}
                    </span>
                  </li>
                ))}
              </ul>
            ) : <p className="font-body text-[13px] text-slate-500 mt-2">Nothing uploaded yet — your first paper starts the bank for your school.</p>}
          </div>
        </div>
      )}

      <div className="rounded-3xl border border-amber-100 bg-white p-4" data-testid="qb-evidence">
        <p className="font-heading text-[13px] font-semibold text-slate-800 flex items-center gap-1.5">
          <ScrollText className="w-4 h-4 text-amber-500" /> Questions with real past-paper evidence
        </p>
        {evidence?.questions?.length ? (
          <>
            <ul className="mt-2 space-y-1.5">
              {evidence.questions.map(q => (
                <li key={q.evidence_id} className="rounded-2xl border border-amber-100 bg-amber-50/60 p-2.5" data-testid={`qb-evidence-${q.evidence_id}`}>
                  <p className="font-body text-[13px] text-slate-800">{q.question}</p>
                  <p className="font-body text-[11px] text-amber-800">
                    {q.label} · {q.past_paper_count} paper{q.past_paper_count === 1 ? '' : 's'}
                    {q.marks ? ` · ${q.marks} marks` : ''}{q.years?.length ? ` · ${q.years.join(', ')}` : ''}
                  </p>
                </li>
              ))}
            </ul>
            <p className="font-body text-[11px] text-slate-500 mt-2 flex items-start gap-1.5">
              <ShieldCheck className="w-3.5 h-3.5 text-emerald-500 mt-0.5 flex-shrink-0" /> {evidence.disclaimer}
            </p>
          </>
        ) : (
          <p className="font-body text-[13px] text-slate-500 mt-2" data-testid="qb-evidence-empty">
            {evidence?.empty_message || "We're still tagging past papers for your class — nothing here is guessed."}
          </p>
        )}
      </div>
    </div>
  );
}
